import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { X, User, Mail, Phone, Calendar, Home, MapPin, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const UserDetails = ({ user, onClose }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);

      // Fetch user with related data based on role
      let query = supabase?.from('user_profiles')?.select(`
          *,
          properties:properties(
            id, title, property_type, price, address, status, created_at
          ),
          appointments_as_client:appointments!appointments_client_id_fkey(
            id, appointment_type, status, scheduled_date,
            property:properties(title, address)
          ),
          appointments_as_agent:appointments!appointments_agent_id_fkey(
            id, appointment_type, status, scheduled_date,
            property:properties(title, address),
            client:user_profiles!appointments_client_id_fkey(full_name, email)
          ),
          property_inquiries:property_inquiries(
            id, message, status, created_at,
            property:properties(title, address)
          )
        `)?.eq('id', user?.id)?.single();

      const { data, error } = await query;

      if (error) throw error;

      // If user is a notary, also fetch notary details
      if (data?.role === 'notary') {
        const { data: notaryData } = await supabase?.from('notaries')?.select('*')?.eq('user_id', user?.id)?.single();
        
        data.notary_details = notaryData;
      }

      setUserDetails(data);
    } catch (error) {
      console.error('Error fetching user details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchUserDetails();
    }
  }, [user?.id]);

  const roleColors = {
    admin: 'bg-red-100 text-red-800',
    agent: 'bg-blue-100 text-blue-800',
    notary: 'bg-green-100 text-green-800',
    client: 'bg-gray-100 text-gray-800'
  };

  const statusColors = {
    scheduled: 'bg-yellow-100 text-yellow-800',
    confirmed: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-90vh overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold">Détails de l'utilisateur</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* User Profile Section */}
          <div className="flex items-start space-x-4 mb-6">
            <div className="flex-shrink-0">
              {userDetails?.avatar_url ? (
                <img
                  src={userDetails?.avatar_url}
                  alt={userDetails?.full_name}
                  className="h-16 w-16 rounded-full"
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-300 flex items-center justify-center">
                  <User className="h-8 w-8 text-gray-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-xl font-semibold text-gray-900">
                  {userDetails?.full_name}
                </h2>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${roleColors?.[userDetails?.role] || 'bg-gray-100 text-gray-800'}`}>
                  {userDetails?.role}
                </span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  userDetails?.is_active
                    ? 'bg-green-100 text-green-800' :'bg-red-100 text-red-800'
                }`}>
                  {userDetails?.is_active ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4" />
                  <span>{userDetails?.email}</span>
                </div>
                {userDetails?.phone && (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span>{userDetails?.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>
                    Inscrit le {format(new Date(userDetails?.created_at), 'dd MMMM yyyy', { locale: fr })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notary Details */}
          {userDetails?.role === 'notary' && userDetails?.notary_details && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-green-900 mb-2">Informations Notariales</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Numéro de licence:</strong> {userDetails?.notary_details?.license_number}</p>
                <p><strong>Adresse du bureau:</strong> {userDetails?.notary_details?.office_address}</p>
                {userDetails?.notary_details?.specializations && (
                  <p><strong>Spécialisations:</strong> {userDetails?.notary_details?.specializations?.join(', ')}</p>
                )}
                <p><strong>Vérifié:</strong> {userDetails?.notary_details?.is_verified ? 'Oui' : 'Non'}</p>
              </div>
            </div>
          )}

          {/* Tabs for different data sections */}
          <div className="space-y-6">
            {/* Properties Section (for clients) */}
            {userDetails?.role === 'client' && userDetails?.properties?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Home className="h-5 w-5 mr-2" />
                  Propriétés ({userDetails?.properties?.length})
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {userDetails?.properties?.map((property) => (
                    <div key={property?.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{property?.title}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          property?.status === 'available' ? 'bg-green-100 text-green-800' :
                          property?.status === 'sold'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {property?.status}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2" />
                          {property?.address}
                        </div>
                        <p><strong>Type:</strong> {property?.property_type}</p>
                        <p><strong>Prix:</strong> {property?.price?.toLocaleString()} FCFA</p>
                        <p><strong>Créée le:</strong> {format(new Date(property?.created_at), 'dd/MM/yyyy')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client Appointments */}
            {userDetails?.appointments_as_client?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Rendez-vous en tant que client ({userDetails?.appointments_as_client?.length})
                </h3>
                <div className="space-y-3">
                  {userDetails?.appointments_as_client?.slice(0, 5)?.map((appointment) => (
                    <div key={appointment?.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{appointment?.appointment_type}</h4>
                          <p className="text-sm text-gray-600">{appointment?.property?.title}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${statusColors?.[appointment?.status] || 'bg-gray-100 text-gray-800'}`}>
                          {appointment?.status}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {format(new Date(appointment?.scheduled_date), 'dd/MM/yyyy à HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Agent Appointments */}
            {userDetails?.appointments_as_agent?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Rendez-vous gérés en tant qu'agent ({userDetails?.appointments_as_agent?.length})
                </h3>
                <div className="space-y-3">
                  {userDetails?.appointments_as_agent?.slice(0, 5)?.map((appointment) => (
                    <div key={appointment?.id} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-gray-900">{appointment?.appointment_type}</h4>
                          <p className="text-sm text-gray-600">
                            Client: {appointment?.client?.full_name}
                          </p>
                          <p className="text-sm text-gray-600">{appointment?.property?.title}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${statusColors?.[appointment?.status] || 'bg-gray-100 text-gray-800'}`}>
                          {appointment?.status}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        {format(new Date(appointment?.scheduled_date), 'dd/MM/yyyy à HH:mm')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Inquiries */}
            {userDetails?.property_inquiries?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Demandes de renseignements ({userDetails?.property_inquiries?.length})
                </h3>
                <div className="space-y-3">
                  {userDetails?.property_inquiries?.slice(0, 3)?.map((inquiry) => (
                    <div key={inquiry?.id} className="border rounded-lg p-3">
                      <div className="mb-2">
                        <h4 className="font-medium text-gray-900">{inquiry?.property?.title}</h4>
                        <p className="text-sm text-gray-600">{inquiry?.property?.address}</p>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{inquiry?.message}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Statut: {inquiry?.status}</span>
                        <span>{format(new Date(inquiry?.created_at), 'dd/MM/yyyy')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetails;